<template>
  <transition name="fade" mode="out-in">
    <div v-if="displayed" class="dark-overlay v-centerer" @click.stop="$emit('close')">
      <div class="dark-overlay-content xlarge hide-indicators" @click.stop="">
        <CloseButton @click.native="$emit('close')" />
        <h2 class="basic-sub-title hide-indicators__title">
          {{ $t('message.HIDE_SOME_INDICATORS') }}
        </h2>
        <div class="hide-indicators__content v-centerer">
          <div class="hide-indicators__group-options">
            <h3>{{ $t('message.GROUPED_SELECTION') }}</h3>
            <div class="legend">
              {{ $t('message.INDIC_INPUT_INTERVAL_SETTINGS') }}
            </div>
            <div v-for="period in allInputPeriods" :key="period.key">
              <div class="v-centerer">
                <Checkbox
                  :value="period.selected"
                  :title="period.numberedTitle"
                  @input="selectIndicators(period.indicators)"
                />
                <div
                  class="hide-indicators__group-options__period"
                  :class="`hide-indicators__group-options__period--${period.key}`"
                >
                  <span>{{ period.title.substring(0, 1) }}</span>
                </div>
              </div>
            </div>
            <div class="legend">
              {{ $t('message.TYPE') }}
            </div>
            <div v-for="(type, index) in allTypes" :key="index">
              <Checkbox
                :value="type.selected"
                :title="type.title"
                @input="selectIndicators(type.indicators)"
              />
            </div>
          </div>
          <div class="hide-indicators__themes">
            <div v-for="theme in themes" :key="theme.id" class="hide-indicators__theme">
              <div class="hide-indicators__theme__name v-centerer">
                <span class="legend">{{ theme.name }}</span>
                <Checkbox
                  :value="!theme.indicators.some(({ id }) => !value.includes(id))"
                  :title="!theme.indicators.some(({ id }) => !value.includes(id))
                    ? $t('message.UNSELECT_ALL')
                    : $t('message.SELECT_ALL')"
                  @input="selectIndicators(theme.indicators)"
                />
              </div>
              <div class="hide-indicators__theme__indicators">
                <div v-for="indicator in theme.indicators" :key="indicator.id">
                  <div class="v-centerer">
                    <Checkbox
                      :value="value.includes(indicator.id)"
                      :title="indicator.name"
                      @input="hideIndicator(indicator)"
                    />
                    <div
                      class="hide-indicators__theme__indicators__period"
                      :class="`hide-indicators__theme__indicators__period--${indicator.inputPeriodUnit}`"
                    >
                      <span>{{ inputPeriodsPerKey[indicator.inputPeriodUnit].title.substring(0, 1) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * Allow ticking multiple indicators using an array of IDs as value
 */

import { difference, uniq, union } from 'lodash';
// Components
import Checkbox from '@/components/checkbox.vue';
import CloseButton from '@/components/close-button.vue';

export default {

  name: 'HideIndicatorsOptions',

  components: {
    Checkbox,
    CloseButton,
  },

  props: {
    displayed: Boolean,
    value: {
      type: Array,
      required: true,
    },
    themes: {
      type: Array,
      required: true,
    },
  },

  computed: {
    /** Array: Available chart point input periods */
    allInputPeriods() {
      const ps = [
        { key: 'd', title: this.$t('message.DAY') },
        { key: 'w', title: this.$t('message.WEEK') },
        { key: 'M', title: this.$t('message.MONTH') },
      ];
      const periods = ps.reduce((all, period) => ({ ...all, [period.key]: { ...period, indicators: [] } }), {});
      this.themes.forEach((theme) => {
        theme.indicators.forEach((indic) => {
          periods[indic.inputPeriodUnit].indicators.push(indic);
        });
      });
      Object.keys(periods).forEach((period) => {
        const p = periods[period];
        p.selected = p.indicators.length > 0 && !p.indicators.some(({ id }) => !this.value.includes(id));
        p.numberedTitle = `${p.title} (${p.indicators.length})`;
      });
      return Object.values(periods);
    },
    /** Object: Same data as above but referenced per period key */
    inputPeriodsPerKey() {
      return this.allInputPeriods.reduce((all, period) => ({ ...all, [period.key]: period }), {});
    },
    /** Array: Available indicator types */
    allTypes() {
      const allTypes = [
        {
          title: this.$t('message.INDIC_SETTINGS_REGULAR_INDICATOR'),
          indicators: this.themes.flatMap(theme => theme.indicators.filter(({ isGw }) => isGw === 0)),
        },
        {
          title: this.$t('message.INDIC_SETTINGS_GW_INDICATOR'),
          indicators: this.themes.flatMap(theme => theme.indicators.filter(({ isGw }) => isGw === 1)),
        },
      ];
      allTypes.forEach((type) => {
        type.selected = type.indicators.length > 0 && !type.indicators.some(({ id }) => !this.value.includes(id));
        type.title = `${type.title} (${type.indicators.length})`;
      });
      return allTypes.filter(type => type.indicators.length > 0);
    },
  },

  methods: {
    /** Hide/Show an indicator */
    hideIndicator(indicator) {
      const hiddenIndicators = this.value.slice();
      const idx = hiddenIndicators.findIndex(id => id === indicator.id);
      if (idx > -1) {
        hiddenIndicators.splice(idx, 1);
      } else {
        hiddenIndicators.push(indicator.id);
      }
      this.$emit('input', hiddenIndicators);
    },
    /** (un)select multiple indicators */
    selectIndicators(indicators) {
      const doSelect = indicators.some(({ id }) => !this.value.includes(id));
      const ids = indicators.map(indic => indic.id);
      const newVal = doSelect ? uniq(union(this.value, ids)) : difference(this.value, ids);
      this.$emit('input', newVal);
    },
  },

};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.dark-overlay {
  .dark-overlay-content.hide-indicators {
    padding: 0px;

    h2.basic-sub-title.hide-indicators__title {
      margin: 0;
      padding-left: 1rem;

      &:after {
        margin: 0 0 1rem -1rem;
        width: calc(100% + 1rem);
      }
    }
  }
}

.hide-indicators {

  h2.basic-sub-title#{&}__title {
    font-size: 1.3em;
    height: 1.5em;
    margin: 0.5em 0px;
    text-align: center;
    padding: 0;

    &:after {
      content: " ";
      display: block;
      background: $color-gray-background-hover;
      height: 1px;
      transform: translateY(1rem);
      margin: 0;
    }
  }
  &__content {
    height: calc(100% - 5em);
    box-sizing: border-box;
  }

  &__group-options {
    flex: 0 0 25%;
    margin: 0 1em;
    box-sizing: border-box;
    min-width: 0px;

    h3 {
      margin-bottom: 0.5em;
    }
    .legend {
      margin-top: 0.5em;
    }
  }

  &__themes {
    flex: 1;
    height: 100%;
    overflow: auto;
    box-sizing: border-box;
    min-width: 0px;
  }

  &__theme {

    &__indicators {
      position: relative;
      padding-left: 1em;
      padding-right: 10em;

      &::before {
        content: " ";
        display: block;
        position: absolute;
        top: 0px;
        bottom: 0px;
        left: 0.4em;
        width: 0.2em;
        background: $color-blue-text;
        opacity: 0.5;
      }

      .checkbox {
        margin-left: 0.5em;
      }

      & &__period {
        margin-left: 2.5em;
      }
    }
    &__name {
      margin: 0.7em 0 0.4em 0;

      .legend {
        margin-right: 1em;
      }
    }
  }

  &__theme__indicators__period, &__group-options__period {
    border: 1px solid;
    margin-left: 1.5em;
    font-size: 0.75em;
    padding: 0.1em 0.2em;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1em;
    height: 1.3em;
    opacity: 0.7;
    font-weight: 700;

    &--d {
      border-color: #BC0070;
      color: #BC0070;
    }

    &--w {
      border-color: #0000AA;
      color: #0000AA;
    }

    &--M {
      border-color: #048E2B;
      color: #048E2B;
    }
  }
}
</style>
